"use client"

import React, { useCallback, useRef } from 'react'
import {
  computeFieldInsert,
  readSelection,
  restoreCaret,
  setNativeValue,
  type SavedSelection,
  type TextField,
} from '@/lib/voice/insert-into-field'
import { normalizeSingleLine } from '@/lib/voice/insert-text'

export interface UseFieldVoiceOptions {
  /** Явное применение нового значения (основной путь). Без него — fallback через нативный setter + `input`. */
  onVoiceInsert?: (next: string) => void
  /** Текущее значение, если элемент недоступен (обычно не нужно — читаем из DOM) */
  getValue?: () => string
  /**
   * Неконтролируемое поле (`defaultValue`): значение всегда пишется в DOM нативным setter'ом,
   * а `onVoiceInsert` (если есть) вызывается дополнительно — например, для onBlur-сохранения.
   */
  uncontrolled?: boolean
  /** Однострочное поле (Input, поиск, название): убирается точка в конце и переводы строк */
  singleLine?: boolean
}

/**
 * Общая логика вставки для VoiceInput / VoiceTextarea / raw textarea:
 * запоминает позицию курсора (blur/select/keyup/click), вставляет текст на её место,
 * возвращает фокус и каретку.
 */
export function useFieldVoice<T extends TextField>({ onVoiceInsert, getValue, uncontrolled, singleLine }: UseFieldVoiceOptions = {}) {
  const fieldRef = useRef<T | null>(null)
  const selectionRef = useRef<SavedSelection | null>(null)
  const onVoiceInsertRef = useRef(onVoiceInsert)
  onVoiceInsertRef.current = onVoiceInsert
  const getValueRef = useRef(getValue)
  getValueRef.current = getValue
  const uncontrolledRef = useRef(uncontrolled)
  uncontrolledRef.current = uncontrolled
  const singleLineRef = useRef(singleLine)
  singleLineRef.current = singleLine

  const rememberSelection = useCallback(() => {
    const sel = readSelection(fieldRef.current)
    if (sel) selectionRef.current = sel
  }, [])

  const insert = useCallback((rawText: string) => {
    const el = fieldRef.current
    const text = singleLineRef.current ? normalizeSingleLine(rawText) : rawText
    const value = el ? el.value : (getValueRef.current?.() ?? '')
    const { next, caret } = computeFieldInsert(el, value, text, selectionRef.current)
    if (next === value) return
    if (uncontrolledRef.current) {
      if (el) setNativeValue(el, next)
      onVoiceInsertRef.current?.(next)
    } else if (onVoiceInsertRef.current) {
      onVoiceInsertRef.current(next)
    } else if (el) {
      setNativeValue(el, next)
    }
    selectionRef.current = { start: caret, end: caret }
    restoreCaret(el, caret)
  }, [])

  /** Пропсы-обработчики для поля; оборачивают пользовательские, если переданы. */
  const bindSelectionTracking = useCallback(<E extends React.SyntheticEvent<T>>(
    handlers: {
      onBlur?: (e: React.FocusEvent<T>) => void
      onSelect?: (e: E) => void
      onKeyUp?: (e: React.KeyboardEvent<T>) => void
      onClick?: (e: React.MouseEvent<T>) => void
    },
  ) => ({
    onBlur: (e: React.FocusEvent<T>) => { rememberSelection(); handlers.onBlur?.(e) },
    onSelect: (e: E) => { rememberSelection(); handlers.onSelect?.(e) },
    onKeyUp: (e: React.KeyboardEvent<T>) => { rememberSelection(); handlers.onKeyUp?.(e) },
    onClick: (e: React.MouseEvent<T>) => { rememberSelection(); handlers.onClick?.(e) },
  }), [rememberSelection])

  return { fieldRef, insert, rememberSelection, bindSelectionTracking }
}
